<template>
  <div class="main">
    <NavBar />
    <!-- Privacy navigation -->
    <nav>
      <div
        v-if="menuItems?.length > 1"
        class="sub-menu">
        <ContentNavigation>
          <NuxtLink
            v-for="menuItem of menuItems"
            :key="menuItem._path"
            :to="menuItem._path">
            {{ getLanguageLabel(menuItem._path) || menuItem.title }}
          </NuxtLink>
        </ContentNavigation>
      </div>
    </nav>

    <div class="body">
      <slot />
    </div>

    <FooterBar />
  </div>
</template>

<script setup lang="ts">
  //  hardcode import from vue-router #imports has a bug
  import type { NavItem } from '@nuxt/content'
  import { computed, useAsyncData, useHead, useRoute } from '#imports'

  const route = useRoute()

  // Head
  useHead({
    title: 'Outfit7 Neo',
    meta: [
      { name: 'description', content: 'We make great games for everyone!' },
      { name: 'theme-color', content: '#333333' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
      { name: 'og:title', content: 'Outfit7 Neo' },
      { name: 'og:description', content: 'We make great games for everyone!' },
      // { name: 'og:image', content: 'https://cdn.outfit7.com/web/transfered/HDS.png' },
      { name: 'og:url', content: 'www.outfit7neo.com' }
    ]
  })

  const availableLanguages = {
    de: 'Deutsch',
    en: 'English',
    it: 'Italiano',
    es: 'Español',
    fr: 'Français',
    id: 'Bahasa Indonesia',
    ja: '日本語',
    ko: '한국어',
    pt: 'Português',
    ru: 'Pусский',
    tr: 'Türkçe',
    zh: '中文（简体）',
    'zh-trad': '中文 (繁体)'
  }

  const { data: navigation } = await useAsyncData('navigation', () => fetchContentNavigation())

  const menuItems = computed(() => {
    if (route.params.slug) {
      const slug = Array.isArray(route.params.slug) ? route.params.slug : [route.params.slug]
      const language = slug[slug.length - 1]

      if (!(language in availableLanguages)) {
        return []
      }

      const currentDirectory = `/${slug.slice(0, -1).join('/')}`
      const filteredNavigation = findNavigationByPath(navigation.value, currentDirectory)
      return filteredNavigation?.children || []
    }

    return []
  })

  function findNavigationByPath(navItems: NavItem[], targetPath: string) {
    for (const item of navItems || []) {
      if (item._path === targetPath) {
        return item
      }

      const nestedMatch: NavItem = findNavigationByPath(item.children, targetPath)
      if (nestedMatch) {
        return nestedMatch
      }
    }
  }

  function getLanguageLabel(path: string) {
    const language = path.split('/').pop() as keyof typeof availableLanguages
    return availableLanguages[language]
  }
</script>

<style scoped lang="scss">
  @import '~/assets/design';

  .main {
    position: relative;
    display: block;
    width: 100%;
    min-width: $size-content-width-min;
    min-height: 100%;
    padding-top: 10px;
    // Margin to account for the fixed header height
    margin: 70px auto 0;
    overflow-x: hidden;
    background: #151515;

    .sub-menu {
      padding: 0;
      margin-bottom: 0;
      text-align: center;
      background-color: #fff;
      border: 1px solid #dbdbdb;
      position: relative;
      z-index: 9;
      a {
        display: inline-block;
        padding: 10px 10px;
        font-family: 'O7TF Display';
        font-size: 14px;
        color: #000;
        text-align: center;
        &.router-link-active {
          font-family: 'O7TF Display';
          font-size: 15px;
          color: #ed3023;
        }
      }
    }

    .body {
      height: 100%;
      padding: env(safe-area-inset-top) 0 0 0 !important;
      margin: -2px auto 0;
      background: #151515;
    }
  }
</style>
